import React, { useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";
import { Upload, ChevronDown } from "lucide-react";
import { Modal, Row, Col, Spinner } from "react-bootstrap";
import { fetchSops, fetchDashboardStats, fetchUsers, type AdminUser, createSop, createUser } from "../../services/api";
import type { SopItem } from "../../types";
import Preloader from "../../components/Preloder";
import { useUpload } from "../../contexts/UploadContext";
import { useTimezoneFormatting } from "../../hooks/useTimezoneFormatting";
import { logger } from "../../utils/logger";
import totalSOP from "../../assets/icons/totalSOP.png";
import totalUsers from "../../assets/icons/totalUsers.png";
import chatBotInteraction from "../../assets/icons/chatBotInteraction.png";
import queriedSOP from "../../assets/icons/queriedSOP.png";
import latestFeed from "../../assets/icons/latestFeed.png";
import quickActions from "../../assets/icons/quickActions.png";
import sopUploadSuccessImage from "../../assets/icons/sop-upload-success-image.gif";

export const AdminDashboard: React.FC = () => {
    const { formatToLocal } = useTimezoneFormatting();
    const [showAddSopModal, setShowAddSopModal] = useState<boolean>(false);
    const [showAddUserModal, setShowAddUserModal] = useState<boolean>(false);
    const [latest, setLatest] = useState<SopItem[]>([]);
    const [latestLoading, setLatestLoading] = useState<boolean>(false);
    const [latestError, setLatestError] = useState<string | null>(null);
    const [statSops, setStatSops] = useState<string>("-");
    const [statUsers, setStatUsers] = useState<string>("-");
    const [statInteractions, setStatInteractions] = useState<string>("-");
    const [statMostQueried, setStatMostQueried] = useState<string>("-");
    const [statsLoading, setStatsLoading] = useState<boolean>(false);

    // Global upload context
    const { getUploads, closeSuccessModal, closeIndicator, showSuccessModal: globalShowSuccessModal, successSopTitle: globalSuccessSopTitle } = useUpload();
    const uploads = getUploads();

    // Local success modal state
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [successSopTitle, setSuccessSopTitle] = useState("");
    const hasShownSuccessModal = useRef(false);

    // Refresh mechanism for stats and latest activities (only on user actions)
    const [refreshKey, setRefreshKey] = useState(0);

    // Watch for upload completion from global context
    useEffect(() => {
        if (globalShowSuccessModal && globalSuccessSopTitle && !hasShownSuccessModal.current) {
            setSuccessSopTitle(globalSuccessSopTitle);
            setShowSuccessModal(true);
            hasShownSuccessModal.current = true;
            // Close the global success modal state so it doesn't show again on other pages
            closeSuccessModal();
            // Refresh dashboard data
            setTimeout(() => {
                setRefreshKey(prev => prev + 1);
            }, 500);
        }
    }, [globalShowSuccessModal, globalSuccessSopTitle, closeSuccessModal]);

    useEffect(() => {
        let cancelled = false;
        async function loadLatest() {
            setLatestLoading(true);
            setLatestError(null);
            try {
                const res = await fetchSops({ page: 1, pageSize: 4, search: "" });
                if (cancelled) return;


                const items: SopItem[] = res?.items || res?.data?.items || [];
                setLatest(items);

                // Only set error if there's an actual error, not just an empty result
                if (res?.error || res?.status === 'error') {
                    setLatestError(res.message || res.error || "Failed to load recent activity");
                } else {
                    setLatestError(null); // Clear any previous errors
                }
            } catch (e: any) {
                if (cancelled) return;
                setLatest([]);
                setLatestError("No recent activity");
            } finally {
                if (!cancelled) setLatestLoading(false);
            }
        }
        loadLatest();
        return () => { cancelled = true; };
    }, [refreshKey]); // Add refreshKey dependency

    useEffect(() => {
        let cancelled = false;
        async function loadStats() {
            setStatsLoading(true);
            try {
                const res = await fetchDashboardStats();
                if (cancelled) return;
                setStatSops(String(res?.total_sops ?? '-'));
                setStatUsers(String(res?.total_users ?? '-'));
                setStatInteractions(String(res?.chatbot_interactions_today ?? '-'));
                setStatMostQueried(String(res?.most_queried_sop ?? '-'));

                // Backend now uses MongoDB as source of truth, so we can trust total_sops
                // No need for fallback counting since backend is reliable
            } catch {
                if (cancelled) return;
                setStatSops('-');
                setStatUsers('-');
                setStatInteractions('-');
                setStatMostQueried('-');
            } finally {
                if (!cancelled) setStatsLoading(false);
            }
        }
        loadStats();
        return () => { cancelled = true; };
    }, [refreshKey]); // Add refreshKey dependency

    // Show preloader while data is loading
    if (statsLoading || latestLoading) {
        return <Preloader />;
    }

    return (
        <>


            {/* Stats */}
            <Row>
                <Col xs={12} sm={6} lg={3} className="mb-4">
                    <StatCard title={"Total SOPs\nUploaded"} value={statSops} iconSrc={totalSOP} />
                </Col>
                <Col xs={12} sm={6} lg={3} className="mb-4">
                    <StatCard title={"Number of Total\nUsers"} value={statUsers} iconSrc={totalUsers} />
                </Col>
                <Col xs={12} sm={6} lg={3} className="mb-4">
                    <StatCard title={"Number of Chatbot\nInteraction Today"} value={statInteractions} iconSrc={chatBotInteraction} />
                </Col>
                <Col xs={12} sm={6} lg={3} className="mb-4">
                    <StatCard title={"Most Queried SOP"} value={statMostQueried} iconSrc={queriedSOP} valueVariant="capsule" />
                </Col>
            </Row>

            {/* Activity + Quick Actions */}
            <Row>
                {/* Latest Activities */}
                <Col lg={6}>
                    <div className="db-big-card">
                        <div className="db-big-card-header">
                            <img src={latestFeed} alt="Latest feed" />
                            <span className="text-14-bold grey-800">
                                Latest Activities Feed
                            </span>
                        </div>
                        <div className="db-big-card-body">
                            {latestError ? (
                                <div className="text-center text-danger py-5">{latestError}</div>
                            ) : latest.length === 0 ? (
                                <div className="text-center text-muted py-5">No recent activity</div>
                            ) : (
                                latest.map((item) => (
                                    <ActivityItem key={item.id} title={item.title} uploadedOn={item.uploadedOn} formatToLocal={formatToLocal} />
                                ))
                            )}
                        </div>
                    </div>
                </Col>

                {/* Quick Actions */}
                <Col lg={6}>
                    <div className="db-big-card">
                        <div className="db-big-card-header">
                            <img src={quickActions} alt="Quick actions" />
                            <span className="text-14-bold grey-800">
                                Quick Actions
                            </span>
                        </div>
                        <div className="db-big-card-body">

                            {/* Upload New SOP */}
                            <div
                                className="quick-action-item mb-3 cursor-pointer"
                                onClick={() => setShowAddSopModal(true)}
                                title="Upload SOP"
                            >
                                <i className="iconoir-page"></i>
                                <div>
                                    <h4 className="grey-800 mb-1">Upload New SOP</h4>
                                    <p className="text-14-reg grey-500 mb-0">Add procedural documentation</p>
                                </div>
                            </div>

                            {/* Add New User */}
                            <div className="quick-action-item" onClick={() => setShowAddUserModal(true)}>
                                <i className="iconoir-user-plus"></i>
                                <div>
                                    <h4 className="grey-800 mb-1">Add New User</h4>
                                    <p className="text-14-reg grey-500 mb-0">Create new user account</p>
                                </div>
                            </div>

                        </div>
                    </div>
                </Col>
            </Row>

            {/* Add New SOP Modal */}
            <AddNewSopModal
                show={showAddSopModal}
                onClose={() => setShowAddSopModal(false)}
                onSuccess={() => {
                    // Refresh dashboard data after successful SOP creation
                    setTimeout(() => {
                        setRefreshKey(prev => prev + 1);
                    }, 500);
                }}
            />

            {/* Add New User Modal */}
            <AddNewUserModal
                show={showAddUserModal}
                onClose={() => setShowAddUserModal(false)}
                onSuccess={() => {
                    // Refresh dashboard data after successful user creation
                    setTimeout(() => {
                        setRefreshKey(prev => prev + 1);
                    }, 500);
                }}
            />

            {/* SOP Upload Success Modal */}
            <SopUploadSuccessModal
                show={showSuccessModal}
                onClose={() => {
                    setShowSuccessModal(false);
                    hasShownSuccessModal.current = false; // Reset for future uploads
                }}
                sopTitle={successSopTitle}
            />

        </>
    );
};


function StatCard({ title, value, iconSrc, valueVariant = "default" }: { title: string; value: string; iconSrc: string; valueVariant?: "default" | "capsule" }) {
    return (
        <div className="dashboard-stat-card">
            {/* <div className="position-absolute top-0 start-0 w-100 h-100 rounded-3xl border-top border-start border-danger" style={{borderColor: '#fecaca', pointerEvents: 'none'}} /> */}
            <div className="d-flex flex-column h-100 justify-content-between">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="text-16-reg grey-600 mt-1">{title}</h6>
                    <div className="stat-card-icon">
                        <img src={iconSrc} alt="" style={{ width: '16px', height: '16px' }} />
                    </div>
                </div>
                {valueVariant === "capsule" ? (
                    <div className="mt-1 w-100 text-start">
                        <p
                            className="text-12-bold grey-800"
                            style={{
                                backgroundColor: '#F7F7F7',
                                padding: '12px 16px',
                                borderRadius: '40px',
                                display: 'inline-block',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                maxWidth: '100%',
                                width: '100%'
                            }}
                            title={value}
                        >
                            {value}
                        </p>
                    </div>
                ) : (
                    <h1 className="grey-900">{value}</h1>
                )}
            </div>
        </div>
    );
}

function ActivityItem({ title, uploadedOn, formatToLocal }: { title: string; uploadedOn?: string; formatToLocal: (timestamp: string, options?: Intl.DateTimeFormatOptions) => string }) {
    return (
        <>
            <div className="latest-activity-item">
                <div className="d-flex justify-content-between mb-3">
                    <p className="text-16-bold grey-900">Uploaded SOP</p>
                    <p className="text-12-reg grey-900 d-flex align-items-center">
                        <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <g clip-path="url(#clip0_2416_277)">
                                <path d="M14 8.25781C14.3125 8.47135 14.5911 8.71354 14.8359 8.98438C15.0807 9.25521 15.2917 9.55208 15.4688 9.875C15.6458 10.1979 15.776 10.5391 15.8594 10.8984C15.9427 11.2578 15.9896 11.625 16 12C16 12.6198 15.8828 13.2031 15.6484 13.75C15.4141 14.2969 15.0911 14.7734 14.6797 15.1797C14.2682 15.5859 13.7917 15.9062 13.25 16.1406C12.7083 16.375 12.125 16.4948 11.5 16.5C11.026 16.5 10.5677 16.4297 10.125 16.2891C9.68229 16.1484 9.27604 15.9453 8.90625 15.6797C8.53646 15.4141 8.20833 15.0964 7.92188 14.7266C7.63542 14.3568 7.41406 13.9479 7.25781 13.5H1V1.5H3V0.5H4V1.5H11V0.5H12V1.5H14V8.25781ZM2 2.5V4.5H13V2.5H12V3.5H11V2.5H4V3.5H3V2.5H2ZM7.02344 12.5C7.00781 12.3385 7 12.1719 7 12C7 11.5521 7.0625 11.1172 7.1875 10.6953C7.3125 10.2734 7.5026 9.875 7.75781 9.5H7V8.5H8V9.17188C8.21354 8.90625 8.45052 8.67188 8.71094 8.46875C8.97135 8.26562 9.2526 8.09115 9.55469 7.94531C9.85677 7.79948 10.1719 7.6901 10.5 7.61719C10.8281 7.54427 11.1615 7.50521 11.5 7.5C12.0208 7.5 12.5208 7.58594 13 7.75781V5.5H2V12.5H7.02344ZM11.5 15.5C11.9844 15.5 12.4375 15.4089 12.8594 15.2266C13.2812 15.0443 13.651 14.7943 13.9688 14.4766C14.2865 14.1589 14.5365 13.7891 14.7188 13.3672C14.901 12.9453 14.9948 12.4896 15 12C15 11.5156 14.9089 11.0625 14.7266 10.6406C14.5443 10.2188 14.2943 9.84896 13.9766 9.53125C13.6589 9.21354 13.2891 8.96354 12.8672 8.78125C12.4453 8.59896 11.9896 8.50521 11.5 8.5C11.0156 8.5 10.5625 8.59115 10.1406 8.77344C9.71875 8.95573 9.34896 9.20573 9.03125 9.52344C8.71354 9.84115 8.46354 10.2109 8.28125 10.6328C8.09896 11.0547 8.00521 11.5104 8 12C8 12.4844 8.09115 12.9375 8.27344 13.3594C8.45573 13.7812 8.70573 14.151 9.02344 14.4688C9.34115 14.7865 9.71094 15.0365 10.1328 15.2188C10.5547 15.401 11.0104 15.4948 11.5 15.5ZM12 11.5H13.5V12.5H11V9.5H12V11.5ZM3 8.5H4V9.5H3V8.5ZM5 8.5H6V9.5H5V8.5ZM5 6.5H6V7.5H5V6.5ZM3 10.5H4V11.5H3V10.5ZM5 10.5H6V11.5H5V10.5ZM8 7.5H7V6.5H8V7.5ZM10 7.5H9V6.5H10V7.5ZM12 7.5H11V6.5H12V7.5Z" fill="black" />
                            </g>
                            <defs>
                                <clipPath id="clip0_2416_277">
                                    <rect width="16" height="16" fill="white" transform="translate(0 0.5)" />
                                </clipPath>
                            </defs>
                        </svg>
                        {formatActivityDate(uploadedOn, formatToLocal)}
                    </p>
                </div>
                <div className="activity-item-content" title={title}>
                    {title}
                </div>
            </div>
        </>
    );
}

function formatActivityDate(value?: string, formatToLocal?: (timestamp: string, options?: Intl.DateTimeFormatOptions) => string) {
    if (!value) return "";
    if (formatToLocal) {
        return formatToLocal(value, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    }
    // Fallback to original formatting if formatToLocal not provided
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${mm}/${dd}/${yyyy}, ${hh}:${mi}`;
}

const truncateText = (text: string, limit = 20): string => {
    if (!text) return "";
    if (text.length <= limit) return text;
    if (limit <= 3) return text.slice(0, limit);
    return `${text.slice(0, limit - 3)}...`;
};

// SOP Upload Success Modal Component
function SopUploadSuccessModal({
    show,
    onClose,
    sopTitle
}: {
    show: boolean;
    onClose: () => void;
    sopTitle: string;
}) {
    return (
        <Modal show={show} onHide={onClose} centered backdrop="static">
            <Modal.Header className="border-0 pb-0">
                <i className="iconoir-xmark text-20-reg cursor-pointer ml-auto" onClick={onClose}></i>
            </Modal.Header>
            <Modal.Body className="text-center pt-2 pb-4">
                <img
                    src={sopUploadSuccessImage}
                    alt="SOP Upload Success"
                    className="mx-auto mb-4"
                    style={{ width: '96px', height: '96px' }}
                />
                <h3 className="text-red-600 mb-2">SOP uploaded successfully.</h3>
                <p className="text-16-reg grey-800 mb-0">{truncateText(sopTitle, 20)} is now available to assigned users.</p>
            </Modal.Body>
        </Modal>
    );
}





// Add New SOP Modal Component
function AddNewSopModal({
    show,
    onClose,
    onSuccess,
}: {
    show: boolean;
    onClose: () => void;
    onSuccess?: () => void;
}) {
    const { submitSopUpload } = useUpload();
    const [sopTitle, setSopTitle] = useState("");
    const [sopDescription, setSopDescription] = useState("");
    const [userRole, setUserRole] = useState("");
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isUserRoleDropdownOpen, setIsUserRoleDropdownOpen] = useState(false);
    const [isSpecificUserDropdownOpen, setIsSpecificUserDropdownOpen] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
    const [users, setUsers] = useState<AdminUser[]>([]);
    const [usersLoading, setUsersLoading] = useState(false);
    const userRoleDropdownRef = useRef<HTMLDivElement>(null);
    const specificUserDropdownRef = useRef<HTMLDivElement>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Validation errors
    const [errors, setErrors] = useState<{ title?: string; description?: string; userRole?: string; specificUsers?: string; file?: string }>({});

    // Reset all form data and errors when modal closes
    const resetForm = () => {
        setSopTitle("");
        setSopDescription("");
        setUserRole("");
        setSelectedFile(null);
        setSelectedUsers([]);
        setErrors({});
        setIsUserRoleDropdownOpen(false);
        setIsSpecificUserDropdownOpen(false);
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Checking file type (PDF only)
        if (file.type !== "application/pdf") {
            setErrors(prev => ({ ...prev, file: 'Only PDF files are allowed' }));
            event.target.value = ''; // Clear the input
            return;
        }

        // Checking file size (10MB max)
        const maxSize = 10 * 1024 * 1024; // 10MB in bytes
        if (file.size > maxSize) {
            setErrors(prev => ({ ...prev, file: 'File size must be less than 10MB' }));
            event.target.value = ''; // Clear the input
            return;
        }

        // File is valid
        setSelectedFile(file);
        setErrors(prev => ({ ...prev, file: undefined }));
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        const newErrors: { title?: string; description?: string; userRole?: string; specificUsers?: string; file?: string } = {};
        if (!sopTitle.trim()) newErrors.title = "SOP Title is required";
        if (!sopDescription.trim()) newErrors.description = "SOP Description is required";
        if (!userRole) newErrors.userRole = "User Role is required";
        if (!selectedFile) newErrors.file = "PDF file is required";
        if (selectedUsers.length === 0) newErrors.specificUsers = "At least one specific user is required";

        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) {
            setIsSubmitting(false);
            return;
        }

        const roleMap: Record<string, string> = {
            worker: "worker",
            all: "all",
            manager: "manager",
            field_worker: "worker",
        };
        const normalizedRole = roleMap[userRole] || "worker";
        const file = selectedFile as File;

        try {
            await submitSopUpload({
                file,
                sopTitle: sopTitle.trim(),
                onClose: () => {
                    onClose();
                },
                createMetadata: async (fileId) => {
                    await createSop({
                        file_id: fileId,
                        title: sopTitle.trim(),
                        description: sopDescription.trim(),
                        user_role: normalizedRole,
                        specific_users: selectedUsers,
                        file_name: file.name || "document.pdf",
                    });
                },
                getAdminEmail: async () => {
                    const { fetchUserProfile } = await import("../../services/api");
                    const userProfile = await fetchUserProfile();
                    return userProfile.email;
                },
                onMetadataCreated: () => {
                    resetForm();
                    onSuccess?.();
                },
            });
        } catch (e: unknown) {
            logger.error("[SOP Upload] Dashboard error:", e);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Fetch users when modal opens
    useEffect(() => {
        const loadUsers = async () => {
            try {
                setUsersLoading(true);
                const res = await fetchUsers({ page: 1, pageSize: 100, status: 'active' });
                setUsers(res.items);
            } catch (error) {
                logger.error('Error fetching users:', error);
            } finally {
                setUsersLoading(false);
            }
        };
        loadUsers();
    }, []);

    // Close dropdowns when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (userRoleDropdownRef.current && !userRoleDropdownRef.current.contains(event.target as Node)) {
                setIsUserRoleDropdownOpen(false);
            }
            if (specificUserDropdownRef.current && !specificUserDropdownRef.current.contains(event.target as Node)) {
                setIsSpecificUserDropdownOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const toggleUserSelection = (userEmail: string) => {
        setSelectedUsers(prev => {
            const newSelection = prev.includes(userEmail)
                ? prev.filter(email => email !== userEmail)
                : [...prev, userEmail];

            // Ensure at least one user is always selected
            if (newSelection.length === 0) {
                return prev; // Don't allow deselecting the last user
            }

            return newSelection;
        });
    };

    // Auto-select users when role changes
    const handleRoleChange = (newRole: string) => {
        setUserRole(newRole);
        setErrors(prev => ({ ...prev, userRole: undefined }));

        // Auto-select all users based on the selected role (excluding admin users)
        const filteredUsers = users.filter(user => {
            if (user.role === 'admin') return false; // Exclude admin users
            if (newRole === 'all') return true;
            if (newRole === 'manager') return user.role === 'manager';
            if (newRole === 'worker') return user.role === 'worker';
            return true;
        });

        const userEmails = filteredUsers.map(user => user.email);
        setSelectedUsers(userEmails);

        // Clear specific users error if users are auto-selected
        if (userEmails.length > 0) {
            setErrors(prev => ({ ...prev, specificUsers: undefined }));
        }
    };

    return (
        <>
            <Modal show={show} onHide={() => { if (!isSubmitting) { resetForm(); onClose(); } }} className='std-modal-width add-sop-modal' centered backdrop="static">
                <Modal.Header>
                    <Modal.Title>
                        Add New SOP
                    </Modal.Title>
                    <i className="iconoir-xmark text-20-reg cursor-pointer" onClick={() => { resetForm(); onClose(); }} aria-label="Close (escape if upload is stuck)" title="Close" />
                </Modal.Header>

                <Modal.Body className="px-4 py-4">
                    {/* Form */}
                    <div>
                        {/* SOP Title */}
                        <div className="form-group">
                            <label htmlFor="sopTitle">
                                SOP Title
                            </label>
                            <input
                                type="text"
                                value={sopTitle}
                                onChange={(e) => { setSopTitle(e.target.value); if (e.target.value.trim()) setErrors(prev => ({ ...prev, title: undefined })); }}
                                placeholder="Enter SOP Title"
                                className={`form-control ${errors.title ? 'border-danger' : ''}`}
                                disabled={isSubmitting}
                                readOnly={isSubmitting}
                            />
                            {errors.title && <p className="error-message">{errors.title}</p>}
                        </div>

                        {/* SOP Description */}
                        <div className="form-group">
                            <label htmlFor="sopDescription">
                                SOP Description
                            </label>
                            <textarea
                                value={sopDescription}
                                onChange={(e) => { setSopDescription(e.target.value); if (e.target.value.trim()) setErrors(prev => ({ ...prev, description: undefined })); }}
                                placeholder="Enter SOP Description"
                                rows={4}
                                className={`form-control ${errors.description ? 'border-danger' : ''}`}
                                disabled={isSubmitting}
                                readOnly={isSubmitting}
                            />
                            {errors.description && <p className="error-message">{errors.description}</p>}
                        </div>

                        {/* User Role & Specific Users */}
                        <Row>
                            <Col md={6}>
                                <div className="form-group">
                                    <label htmlFor="userRole">
                                        User Role
                                    </label>
                                    <div className="relative" ref={userRoleDropdownRef}>
                                        <button
                                            type="button"
                                            disabled={isSubmitting}
                                            onClick={() => { if (!isSubmitting) setIsUserRoleDropdownOpen(!isUserRoleDropdownOpen); }}
                                            className={`custom-dropdown-btn ${errors.userRole ? 'border-danger' : ''} ${isSubmitting ? 'cursor-not-allowed opacity-70' : ''}`}
                                        >
                                            <span className={userRole === '' ? 'text-gray-500' : 'text-gray-700'}>
                                                {userRole === '' ? 'Select User Role' : userRole === 'all' ? 'All' : userRole === 'manager' ? 'Manager' : 'Field Worker'}
                                            </span>
                                            <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isUserRoleDropdownOpen ? 'rotate-180' : ''}`} />
                                        </button>
                                        {isUserRoleDropdownOpen && (
                                            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-10">
                                                <div className="py-1">
                                                    <button
                                                        onClick={() => { setUserRole(''); setSelectedUsers([]); setIsUserRoleDropdownOpen(false); setErrors(prev => ({ ...prev, userRole: undefined })); }}
                                                        className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                                                    >
                                                        Select User Role
                                                    </button>
                                                    <div className="border-t border-gray-200 border-dashed"></div>
                                                    <button
                                                        onClick={() => { handleRoleChange('all'); setIsUserRoleDropdownOpen(false); }}
                                                        className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                                                    >
                                                        All
                                                    </button>
                                                    <div className="border-t border-gray-200 border-dashed"></div>
                                                    <button
                                                        onClick={() => { handleRoleChange('manager'); setIsUserRoleDropdownOpen(false); }}
                                                        className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                                                    >
                                                        Manager
                                                    </button>
                                                    <div className="border-t border-gray-200 border-dashed"></div>
                                                    <button
                                                        onClick={() => { handleRoleChange('worker'); setIsUserRoleDropdownOpen(false); }}
                                                        className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                                                    >
                                                        Field Worker
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    {errors.userRole && <p className="error-message">{errors.userRole}</p>}
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="form-group">
                                    <label htmlFor="specificUsers">
                                        Specific Users
                                    </label>
                                    <div className="relative" ref={specificUserDropdownRef}>
                                        <button
                                            type="button"
                                            disabled={isSubmitting}
                                            onClick={() => { if (!isSubmitting) setIsSpecificUserDropdownOpen(!isSpecificUserDropdownOpen); }}
                                            className={`custom-dropdown-btn ${errors.specificUsers ? 'border-danger' : ''} ${isSubmitting ? 'cursor-not-allowed opacity-70' : ''}`}
                                        >
                                            <span className={selectedUsers.length === 0 ? 'text-gray-500' : 'text-gray-700'}>
                                                {selectedUsers.length === 0 ? 'Select Specific Users' : `${selectedUsers.length} user(s) selected`}
                                            </span>
                                            <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isSpecificUserDropdownOpen ? 'rotate-180' : ''}`} />
                                        </button>
                                        {isSpecificUserDropdownOpen && (
                                            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-10 max-h-48 overflow-y-auto custom-dropdown">
                                                <div className="py-1">
                                                    {usersLoading ? (
                                                        <div className="px-3 py-2 text-sm text-gray-500">
                                                            Loading users...
                                                        </div>
                                                    ) : users.length === 0 ? (
                                                        <div className="px-3 py-2 text-sm text-gray-500">
                                                            No users available
                                                        </div>
                                                    ) : (
                                                        users
                                                            .filter(user => {
                                                                if (user.role === 'admin') return false; // Exclude admin users
                                                                if (userRole === 'all') return true;
                                                                if (userRole === 'manager') return user.role === 'manager';
                                                                if (userRole === 'worker') return user.role === 'worker';
                                                                return true;
                                                            })
                                                            .map((user) => (
                                                                <button
                                                                    key={user.email}
                                                                    onClick={() => {
                                                                        // Prevent deselecting if this is the last selected user
                                                                        if (selectedUsers.includes(user.email) && selectedUsers.length === 1) {
                                                                            return; // Don't allow deselecting the last user
                                                                        }
                                                                        toggleUserSelection(user.email);
                                                                        if (selectedUsers.length > 0 || !selectedUsers.includes(user.email)) {
                                                                            setErrors(prev => ({ ...prev, specificUsers: undefined }));
                                                                        }
                                                                    }}
                                                                    className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 ${selectedUsers.includes(user.email) && selectedUsers.length === 1
                                                                        ? 'text-gray-400 cursor-not-allowed'
                                                                        : 'text-gray-700 hover:bg-gray-100'
                                                                        }`}
                                                                    disabled={selectedUsers.includes(user.email) && selectedUsers.length === 1}
                                                                >
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={selectedUsers.includes(user.email)}
                                                                        onChange={() => { }}
                                                                        disabled={selectedUsers.includes(user.email) && selectedUsers.length === 1}
                                                                        className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                                                                    />
                                                                    {user.name} - {user.role}
                                                                    {selectedUsers.includes(user.email) && selectedUsers.length === 1 && (
                                                                        <span className="text-xs text-gray-400 ml-auto">(Required)</span>
                                                                    )}
                                                                </button>
                                                            ))
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    {errors.specificUsers && <p className="error-message">{errors.specificUsers}</p>}
                                </div>
                            </Col>
                        </Row>

                        {/* PDF File Upload */}
                        <div className="form-group">
                            <label htmlFor="file-upload">
                                PDF File
                            </label>
                            <div className={`border-2 border-dashed ${errors.file ? 'border-red-500' : 'border-gray-300'} rounded-md p-8 text-center transition-colors ${isSubmitting ? 'pointer-events-none opacity-70' : 'hover:border-red-400'}`}>
                                <input
                                    type="file"
                                    accept=".pdf"
                                    onChange={handleFileChange}
                                    className="hidden"
                                    id="file-upload"
                                    disabled={isSubmitting}
                                />
                                <label htmlFor="file-upload" className={isSubmitting ? 'cursor-not-allowed' : 'cursor-pointer'}>
                                    <div className="flex flex-col items-center">
                                        <Upload size={32} className="text-gray-400 mb-2" />
                                        <p className="text-16-bold grey-700 mb-0">Click to upload PDF</p>
                                        <p className="text-14-reg grey-600 mb-0">PDF files only</p>
                                        <p className="text-12-reg grey-400 mt-1">(Max File Size 10mb)</p>
                                    </div>
                                </label>
                                {selectedFile && (
                                    <p className="mt-2 text-sm text-green-600">{selectedFile.name}</p>
                                )}
                            </div>
                            {errors.file && <p className="error-message">{errors.file}</p>}
                        </div>
                    </div>
                    <div className="d-flex justify-content-end gap-3 mt-4">
                        <button
                            type="button"
                            disabled={isSubmitting}
                            onClick={() => { if (!isSubmitting) { resetForm(); onClose(); } }}
                            className={`renegade-btn-secondary mini-btn ${isSubmitting ? 'cursor-not-allowed' : ''}`}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            disabled={isSubmitting}
                            onClick={handleSubmit}
                            className="renegade-btn-primary mini-btn"
                        >
                            {isSubmitting ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Uploading SOP...
                                </>
                            ) : (
                                ' Upload SOP'
                            )}
                        </button>
                    </div>
                </Modal.Body>
            </Modal>

        </>
    );
}

// Add New User Modal Component
function AddNewUserModal({ show, onClose, onSuccess }: { show: boolean; onClose: () => void; onSuccess?: () => void }) {
    const [userName, setUserName] = useState("");
    const [userEmail, setUserEmail] = useState("");
    const [userRole, setUserRole] = useState<"manager" | "worker">("worker");
    const [selectedManager, setSelectedManager] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const [managers, setManagers] = useState<AdminUser[]>([]);
    const [managersLoading, setManagersLoading] = useState(false);
    const [emailError, setEmailError] = useState<string>("");
    const [checkingEmail, setCheckingEmail] = useState(false);
    const [isManagerDropdownOpen, setIsManagerDropdownOpen] = useState(false);
    const managerDropdownRef = useRef<HTMLDivElement>(null);
    const [grantSopAccess, setGrantSopAccess] = useState(false);

    // Validation errors
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    // Reset all form data and errors when modal closes
    const resetForm = () => {
        setUserName("");
        setUserEmail("");
        setUserRole("worker");
        setSelectedManager("");
        setGrantSopAccess(false);
        setEmailError("");
        setCheckingEmail(false);
        setIsSaving(false);
        setErrors({});
    };

    // Validation functions
    const validateForm = () => {
        const newErrors: { [key: string]: string } = {};

        if (!userName.trim()) {
            newErrors.name = 'User Name is required';
        }

        if (!userEmail.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(userEmail)) {
            newErrors.email = 'Email is invalid';
        }

        if (userRole === "worker" && !selectedManager) {
            newErrors.manager = 'Manager assignment is required for Field Workers';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateField = (fieldName: string, value: string) => {
        const newErrors = { ...errors };

        if (fieldName === 'name') {
            if (!value.trim()) {
                newErrors.name = 'User Name is required';
            } else {
                delete newErrors.name;
            }
        } else if (fieldName === 'email') {
            if (!value.trim()) {
                newErrors.email = 'Email is required';
            } else if (!/\S+@\S+\.\S+/.test(value)) {
                newErrors.email = 'Email is invalid';
            } else {
                delete newErrors.email;
            }
        } else if (fieldName === 'manager') {
            if (userRole === "worker" && !value) {
                newErrors.manager = 'Manager assignment is required for Field Workers';
            } else {
                delete newErrors.manager;
            }
        }

        setErrors(newErrors);
    };

    const handleManagerChange = (managerEmail: string) => {
        setSelectedManager(managerEmail);
        validateField('manager', managerEmail);
    };

    // Fetch managers when modal opens
    useEffect(() => {
        const loadManagers = async () => {
            try {
                setManagersLoading(true);
                const res = await fetchUsers({ page: 1, pageSize: 100, role: 'manager', status: 'active' });
                setManagers(res.items);
            } catch (error) {
                logger.error('Error fetching managers:', error);
            } finally {
                setManagersLoading(false);
            }
        };
        loadManagers();
    }, []);

    // Close manager dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (managerDropdownRef.current && !managerDropdownRef.current.contains(event.target as Node)) {
                setIsManagerDropdownOpen(false);
            }
        }

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Debounced email check
    useEffect(() => {
        if (!userEmail.trim()) {
            setEmailError("");
            return;
        }

        const timeoutId = setTimeout(() => {
            checkEmailExists(userEmail);
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [userEmail]);

    // Check if email exists
    const checkEmailExists = async (email: string) => {
        if (!email.trim()) {
            setEmailError("");
            return false;
        }

        try {
            setCheckingEmail(true);
            setEmailError("");
            const res = await fetchUsers({ page: 1, pageSize: 100, search: email.trim() });
            const existingUser = res.items.find(user => user.email.toLowerCase() === email.trim().toLowerCase());

            if (existingUser) {
                setEmailError("Email already exists");
                return true;
            } else {
                setEmailError("");
                return false;
            }
        } catch (error) {
            logger.error('Error checking email:', error);
            setEmailError("Error checking email");
            return false;
        } finally {
            setCheckingEmail(false);
        }
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        // Check if email already exists
        const emailExists = await checkEmailExists(userEmail.trim());
        if (emailExists) {
            return; // Error message is already set by checkEmailExists
        }

        setIsSaving(true);
        setErrors({});

        try {
            await createUser({
                name: userName.trim(),
                email: userEmail.trim(),
                role: userRole,
                assignedManager: userRole === "worker" ? (selectedManager || undefined) : undefined,
                status: 'active',
                grant_sop_access: grantSopAccess,
            });

            toast.success("User created successfully");
            // Call success callback to refresh dashboard
            onSuccess?.();

            resetForm();
            onClose();
        } catch (e: any) {
            const errorMessage = e?.message || "Failed to create user. Please try again.";
            setErrors({ general: errorMessage });
            logger.error('Failed to create user', e);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal show={show} onHide={() => { resetForm(); onClose(); }} className="std-modal-width" centered backdrop="static">
            <Modal.Header>
                <Modal.Title>
                    Add New User
                </Modal.Title>
                <i className="iconoir-xmark text-20-reg cursor-pointer" onClick={() => { resetForm(); onClose(); }}></i>
            </Modal.Header>

            <Modal.Body className="px-4 py-4">
                {/* Form */}
                <div>
                    {/* User Name */}
                    <div className="form-group">
                        <label htmlFor="userName">
                            User Name
                        </label>
                        <input
                            id="userName"
                            type="text"
                            placeholder="Enter user name"
                            value={userName}
                            onChange={(e) => {
                                setUserName(e.target.value);
                                validateField('name', e.target.value);
                            }}
                            className={`form-control ${errors.name ? 'border-danger' : ''}`}
                        />
                        {errors.name && (
                            <p className="error-message">{errors.name}</p>
                        )}
                    </div>

                    {/* User Email ID */}
                    <div className="form-group">
                        <label htmlFor="userEmail">
                            User Email ID
                        </label>
                        <div className="position-relative">
                            <input
                                id="userEmail"
                                type="email"
                                value={userEmail}
                                placeholder="Enter user email"
                                onChange={(e) => {
                                    setUserEmail(e.target.value);
                                    validateField('email', e.target.value);
                                }}
                                className={`form-control ${errors.email || emailError ? 'border-danger' : ''}`}
                            />
                            {checkingEmail && (
                                <div className="position-absolute top-50 end-0 translate-middle-y me-3">
                                    <div className="w-3 h-3 border-2 border-gray-300 border-t-red-600 rounded-full animate-spin"></div>
                                </div>
                            )}
                        </div>
                        {(errors.email || emailError) && (
                            <p className="error-message">{errors.email || emailError}</p>
                        )}
                    </div>

                    {/* User Role Selection */}
                    <div className="row">
                        <div className="col-md-6">
                            <div className="form-group">
                                <label htmlFor="userRole">
                                    User Role
                                </label>
                                <div className="flex bg-white border border-secondary rounded-md p-1" style={{ height: '54px' }}>
                                    <button
                                        type="button"
                                        onClick={() => setUserRole("manager")}
                                        className={`flex-1 px-4 py-2 text-14-bold rounded-md transition-all ${userRole === "manager"
                                            ? "bg-black text-white shadow-sm"
                                            : "text-gray-600 hover:text-gray-800"
                                            }`}
                                    >
                                        Manager
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setUserRole("worker")}
                                        className={`flex-1 px-4 py-2 text-14-bold rounded-md transition-all ${userRole === "worker"
                                            ? "bg-black text-white shadow-sm"
                                            : "text-gray-600 hover:text-gray-800"
                                            }`}
                                    >
                                        Field Worker
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="col-md-6">
                            {/* Select Manager - Only show for Field Worker */}
                            {userRole === "worker" && (
                                <div className="form-group">
                                    <label htmlFor="selectedManager">
                                        Assign Manager
                                    </label>
                                    <div className="relative" ref={managerDropdownRef}>
                                        <button
                                            title={selectedManager}
                                            type="button"
                                            onClick={() => setIsManagerDropdownOpen(!isManagerDropdownOpen)}
                                            className={`custom-dropdown-btn ${errors.manager ? 'border-danger' : ''}`}
                                        >
                                            <span className={`dp-manager-name ${selectedManager === '' ? 'grey-500' : 'grey-800'}`} >
                                                {selectedManager === ''
                                                    ? 'Select Assign Manager'
                                                    : managers.find(m => m.email === selectedManager)
                                                        ? `${managers.find(m => m.email === selectedManager)?.name} (${selectedManager})`
                                                        : 'Select Assign Manager'
                                                }
                                            </span>
                                            <ChevronDown
                                                className={`w-4 h-4 grey-500 transition-transform ${isManagerDropdownOpen ? 'rotate-180' : ''}`}
                                            />
                                        </button>
                                        {isManagerDropdownOpen && (
                                            <div className="position-absolute w-100 mt-1 bg-white border rounded shadow-lg" style={{ zIndex: 10, maxHeight: '250px', overflowY: 'auto' }}>
                                                <div className="py-1">
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            handleManagerChange('');
                                                            setIsManagerDropdownOpen(false);
                                                        }}
                                                        className="w-100 px-3 py-2 text-start text-14-reg grey-600 hover-bg-light"
                                                    >
                                                        Select Assign Manager
                                                    </button>
                                                    {managersLoading && (
                                                        <>
                                                            <div className="border-top border-dashed"></div>
                                                            <div className="px-3 py-2 text-start text-14-reg grey-500">
                                                                Loading managers...
                                                            </div>
                                                        </>
                                                    )}
                                                    {!managersLoading && managers.length === 0 && (
                                                        <>
                                                            <div className="border-top border-dashed"></div>
                                                            <div className="px-3 py-2 text-start text-14-reg grey-500">
                                                                No managers available
                                                            </div>
                                                        </>
                                                    )}
                                                    {!managersLoading && managers.length > 0 && (
                                                        <>
                                                            <div className="border-top border-dashed"></div>
                                                            {managers.map((manager, index) => (
                                                                <div key={manager.id}>
                                                                    <button
                                                                        title={manager.email}
                                                                        type="button"
                                                                        onClick={() => {
                                                                            handleManagerChange(manager.email);
                                                                            setIsManagerDropdownOpen(false);
                                                                        }}
                                                                        className="w-100 px-3 py-2 text-start text-14-reg grey-600 hover-bg-light dp-manager-name"
                                                                    >
                                                                        {manager.name} ({manager.email})
                                                                    </button>
                                                                    {index < managers.length - 1 && (
                                                                        <div className="border-top border-dashed"></div>
                                                                    )}
                                                                </div>
                                                            ))}
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    {errors.manager && (
                                        <p className="error-message">{errors.manager}</p>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="form-group">
                        <label className="form-label flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                className="form-checkbox"
                                checked={grantSopAccess}
                                onChange={(e) => setGrantSopAccess(e.target.checked)}
                            />
                            <span>
                                Grant access to all existing SOPs (based on role)
                            </span>
                        </label>

                        <p className="form-helper-text">
                            This will automatically give the user access to all SOPs they are eligible for based on their role.
                        </p>
                    </div>

                    {/* General Error Message */}
                    {errors.general && (
                        <div className="alert alert-danger" role="alert">
                            {errors.general}
                        </div>
                    )}
                </div>
                <div className="d-flex justify-content-end gap-3 mt-4">
                    <button
                        onClick={() => { resetForm(); onClose(); }}
                        className="renegade-btn-secondary mini-btn"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={isSaving}
                        className="renegade-btn-primary mini-btn"
                    >
                        {isSaving ? 'Adding...' : 'Add New User'}
                    </button>
                </div>
            </Modal.Body>
        </Modal>
    );
}








